import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPaperPlane } from 'react-icons/fa';
import { BsLightning } from 'react-icons/bs';
import '../styles/scrollbar.css';
import { testChatbot } from '../api';
import MobileLayout, { MOBILE_BREAKPOINT } from './MobileLayout';

const Testing = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentOptions, setCurrentOptions] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isFlowEnd, setIsFlowEnd] = useState(false);
  const [showOptions, setShowOptions] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState(null);
  const [chatbotType, setChatbotType] = useState(null);

  // Refs
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Helper functions
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const createMessage = (text, isUser, options = []) => ({
    text,
    isUser,
    options,
    timestamp: new Date()
  });

  const addMessage = (message) => {
    setMessages(prev => [...prev, message]);
  };

  const getAllMessages = () => {
    return messages.map(msg => ({
      role: msg.isUser ? 'user' : 'assistant',
      content: msg.text
    }));
  };

  // Check screen size on mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth < MOBILE_BREAKPOINT);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle option click
  const handleOptionClick = (option) => {
    if (isLoading) return;
    setInputText(option);
    setShowOptions(false);
    handleSendMessage(option);
  };

  // Handle sending messages
  const handleSendMessage = async (forcedInput) => {
    const textToSend = forcedInput || inputText;
    if ((!textToSend.trim() && !forcedInput) || isLoading) return;

    setIsLoading(true);
    setShowOptions(false);
    const userMessage = createMessage(textToSend, true);
    addMessage(userMessage);
    setInputText('');
    setStreamingMessage(null);
    setCurrentOptions([]);

    try {
      const allMessages = [...getAllMessages(), { role: 'user', content: userMessage.text }];
      const response = await testChatbot(allMessages, questionIndex, isFlowEnd, chatbotType);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let currentStreamingMessage = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'content') {
                setIsLoading(false);
                
                if (data.content.startsWith('~!')) {
                  if (currentStreamingMessage) {
                    addMessage(currentStreamingMessage);
                    currentStreamingMessage = null;
                    setStreamingMessage(null);
                  }
                  
                  const structuredData = JSON.parse(data.content.slice(2));
                  const { question, options, is_flow_end, question_number, chatbot_type } = structuredData;
                  
                  currentStreamingMessage = createMessage(question, false);
                  setCurrentOptions(options);
                  setShowOptions(true);
                  setIsFlowEnd(is_flow_end);
                  setQuestionIndex(question_number);
                  setChatbotType(chatbot_type);
                } else {
                  if (!currentStreamingMessage) {
                    currentStreamingMessage = createMessage(data.content, false);
                  } else {
                    currentStreamingMessage = {
                      ...currentStreamingMessage,
                      text: currentStreamingMessage.text + data.content
                    };
                  }
                  setStreamingMessage(currentStreamingMessage);
                }
              } else if (data.type === 'end') {
                if (currentStreamingMessage) {
                  addMessage(currentStreamingMessage);
                  currentStreamingMessage = null;
                  setStreamingMessage(null);
                }
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error getting response:', error);
      addMessage(createMessage('Error: Could not get response.', false));
    } finally {
      setIsLoading(false);
    }
  };

  if (isMobileView) {
    return (
      <MobileLayout 
        pageTitle="Testing"
        restrictionTitle="Testing Environment Needs More Space"
        restrictionMessage="Testing your AI chatbot requires interactive conversations, detailed response analysis, and comprehensive evaluations. A larger screen ensures you can properly assess your chatbot's performance."
        features={[
          "Test complex conversation flows",
          "Analyze responses in real-time",
          "Evaluate multiple test scenarios",
          "View detailed chat history",
          "Access quick-response options"
        ]}
        proTip="For the most effective testing experience, please use an iPad, tablet, laptop, or desktop computer to evaluate your chatbot's performance."
      />
    );
  }

  // Renders the chat header section
  const renderHeader = () => (
    <div className="mb-8 px-12 pt-3">
      <h1 className="text-2xl font-semibold text-gray-800">Testing</h1>
      <p className="text-gray-600">Test and interact with your AI assistant</p>
    </div>
  );

  // Renders the welcome message component
  const renderWelcomeMessage = () => (
    <div className="mb-12 max-w-[850px]">
      <div className="flex items-start gap-2.5">
        <div className="flex-shrink-0">
          <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-100 flex items-center justify-center transform hover:scale-105 transition-transform duration-200">
            <BsLightning className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="flex-1">
          <div className="inline-block bg-white border border-gray-100 shadow-sm text-gray-800 px-5 py-3.5 rounded-2xl rounded-tl-none">
            <p className="text-[15px] leading-relaxed">Welcome to our AI Assistant! How can I help you today?</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Renders the loading indicator
  const renderLoadingIndicator = () => (
    <div className="mb-8 flex items-start gap-2.5">
      <div className="flex-shrink-0">
        <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-100 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
      <div className="flex-1 max-w-[850px]">
        <div className="inline-flex gap-2 bg-white border border-gray-100 shadow-sm text-gray-400 px-5 py-3.5 rounded-2xl rounded-tl-none">
          {[0, 0.2, 0.4].map((delay, i) => (
            <span key={i} className="animate-bounce" style={{ animationDelay: `${delay}s` }}>•</span>
          ))}
        </div>
      </div>
    </div>
  );

  // Renders the input area for sending messages
  const renderInputArea = () => (
    <div className="fixed bottom-10 left-[70px] right-0 bg-white border-t border-gray-100 shadow-lg">
      <div className="px-12 py-4">
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder={isLoading ? "Waiting for response..." : "Type your message here..."}
              disabled={isLoading}
              className={`w-full py-3.5 px-5 pr-12 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-700 bg-gray-50 placeholder-gray-400 transition-all duration-200 ${
                isLoading ? 'cursor-not-allowed opacity-60' : ''
              }`}
            />
            <button
              onClick={handleSendMessage}
              disabled={isLoading || !inputText.trim()}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-2 transition-colors duration-200 ${
                isLoading || !inputText.trim() 
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-400 hover:text-blue-500'
              }`}
            >
              <FaPaperPlane className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEnterpriseBanner = () => (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-blue-600 to-blue-700 px-12 py-2 z-10">
      <div className="flex justify-between items-center max-w-6xl mx-auto">
        <p className="text-sm text-white">
          <span className="font-semibold">Need enterprise features?</span> Calendar booking, product showcases, human transfer , Lead generation & more.
        </p>
        <a 
          href="https://www.curosai.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm bg-white text-blue-600 hover:bg-blue-50 px-3 py-1 rounded-md font-medium"
        >
          Get Custom Solutions
        </a>
      </div>
    </div>
  );

  // Renders the options separately from messages
  const renderCurrentOptions = () => {
    if (!showOptions || !currentOptions || currentOptions.length === 0) {
      return null;
    }
    
    return (
      <div className="mt-1 mb-2 ml-[46px] max-w-[850px]">
        <div className="flex flex-wrap gap-2">
          {currentOptions.map((option, index) => (
            <button
              key={index}
              onClick={() => handleOptionClick(option)}
              disabled={isLoading}
              className="px-4 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors duration-200"
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="h-[calc(100%-1rem)] bg-gray-50 overflow-hidden">
      {renderHeader()}
      
      <div ref={chatContainerRef} className="h-[calc(100%-9rem)] overflow-y-auto px-12 pb-32 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent hover:scrollbar-thumb-gray-300">
        {renderWelcomeMessage()}
        
        {messages.map((message, index) => (
          <MessageBubble 
            key={index} 
            message={message} 
            isLoading={isLoading}
          />
        ))}
        
        {streamingMessage && (
          <MessageBubble 
            message={streamingMessage} 
            isLoading={isLoading}
          />
        )}
        {isLoading && !streamingMessage && renderLoadingIndicator()}
        {renderCurrentOptions()}
        <div ref={messagesEndRef} />
      </div>

      {renderInputArea()}
      {renderEnterpriseBanner()}
    </div>
  );
};

// Message Bubble Component
const MessageBubble = ({ message, isLoading }) => {
  if (!message.isUser) {
    return (
      <div className="mb-2 flex items-start gap-2.5">
        <div className="flex-shrink-0">
          <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-100 flex items-center justify-center transform hover:scale-105 transition-transform duration-200">
            <BsLightning className="w-5 h-5 text-white" />
          </div>
        </div>
        <div className="flex-1 max-w-[850px]">
          <div className="inline-block bg-white border border-gray-100 shadow-sm text-gray-800 px-5 py-3.5 rounded-2xl rounded-tl-none">
            <p className="text-[15px] leading-relaxed">{message.text}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2 flex justify-end">
      <div className="flex items-start max-w-[850px] ml-auto">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-5 py-3.5 rounded-2xl rounded-tr-none shadow-sm">
          <p className="text-[15px] leading-relaxed">{message.text}</p>
        </div>
      </div>
    </div>
  );
};

export default Testing; 